
import React, { useState } from 'react';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, CarouselApi } from '@/components/ui/carousel';
import { Card, CardContent } from '@/components/ui/card';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { Eye, Palette, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import DemoModal from './DemoModal';

const AILandingPagesGallery = () => {
  const { isMobile } = useBreakpoint();
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState<{
    title: string;
    embedUrl: string;
  } | null>(null);

  const demoTemplates = [
    {
      id: 1,
      title: "Videographer Example",
      category: "Creative Services",
      description: "Professional videography services with AI-powered portfolio showcase",
      image: "/lovable-uploads/d8c46f36-8a29-47e4-8738-3a2750a80a20.png",
      color: "from-blue-500 to-purple-600",
      accent: "bg-blue-100 text-blue-800",
      embedUrl: "https://app.askspot.ai/share-spotlight/68233ef50df59579d8788cd3?embed=true"
    },
    {
      id: 2,
      title: "Chiropractic Example",
      category: "Healthcare",
      description: "Professional chiropractic services with personalized care",
      image: "/lovable-uploads/be75fa4e-063a-4bc4-bf39-b785d90611bc.png",
      color: "from-green-500 to-teal-600",
      accent: "bg-green-100 text-green-800",
      embedUrl: "https://app.askspot.ai/share-spotlight/6850442427d635e3fc3d1a6b?embed=true"
    },
    {
      id: 3,
      title: "Legal Services Platform",
      category: "Professional Services",
      description: "Professional legal consultation and document services",
      image: "/lovable-uploads/330a6326-f7ae-4938-9ed4-76676d599195.png",
      color: "from-slate-500 to-blue-600",
      accent: "bg-slate-100 text-slate-800",
      embedUrl: "https://app.askspot.ai/share-spotlight/685049fe27d635e3fc3d1a77?embed=true"
    }
  ];

  // Track current slide
  React.useEffect(() => {
    if (!api) return;
    
    api.on("select", () => {
      setCurrentIndex(api.selectedScrollSnap());
    });

    // Set up initial index
    setCurrentIndex(api.selectedScrollSnap());
  }, [api]);

  const handleDemoClick = (template: typeof demoTemplates[0]) => {
    if (template.embedUrl) {
      setSelectedDemo({
        title: template.title,
        embedUrl: template.embedUrl
      });
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDemo(null);
  };

  const canScrollPrev = api?.canScrollPrev() ?? false;
  const canScrollNext = api?.canScrollNext() ?? false;

  return (
    <section className="py-12 md:py-16 lg:py-24 bg-gradient-to-br from-white via-[#FEE6D8]/10 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/6 w-64 h-64 md:w-80 md:h-80 bg-[#FEE6D8]/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/6 w-72 h-72 md:w-96 md:h-96 bg-[#003a70]/5 rounded-full blur-3xl"></div>
        <div className="absolute top-2/3 left-1/2 w-48 h-48 md:w-64 md:h-64 bg-gradient-to-r from-[#FEE6D8]/20 to-[#003a70]/5 rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="space-y-8 md:space-y-12">
          
          {/* Section Header */}
          <div className="text-center space-y-4 md:space-y-6">
            <div className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/80 rounded-full shadow-lg border-2 border-[#FEE6D8]/50">
              <Palette className="text-[#003a70]" size={isMobile ? 20 : 24} />
              <span className="text-[#003a70] font-semibold text-sm md:text-lg">Example Genus Card Pages</span>
            </div>
            
            <h2 className={cn(
              "font-bold text-[#003a70] leading-tight px-2",
              isMobile ? "text-2xl sm:text-3xl" : "text-4xl lg:text-5xl"
            )}>
              What Does a Genius Card Page Look Like?
            </h2>
            
            <div className="w-16 md:w-24 h-1 bg-gradient-to-r from-[#FEE6D8] via-[#003a70] to-[#FEE6D8] mx-auto rounded-full shadow-sm"></div>
          </div>

          {/* Mobile Swipe Hint */}
          {isMobile && (
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#003a70]/10 rounded-full text-[#003a70] text-sm font-medium animate-pulse">
                <ChevronLeft size={16} className="animate-bounce" />
                <span>Swipe to explore</span>
                <ChevronRight size={16} className="animate-bounce" />
              </div>
            </div>
          )}

          {/* Carousel Container with Mobile Overflow */}
          <div className="relative">
            {/* Mobile gradient overlays for navigation hints */}
            {isMobile && (
              <>
                {canScrollPrev && (
                  <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none"></div>
                )}
                {canScrollNext && (
                  <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none"></div>
                )}
              </>
            )}

            {/* Mobile Touch Edge Indicators */}
            {isMobile && (
              <>
                {canScrollPrev && (
                  <button
                    onClick={() => api?.scrollPrev()}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-[#003a70]/80 rounded-full flex items-center justify-center text-white shadow-lg"
                  >
                    <ChevronLeft size={16} />
                  </button>
                )}
                {canScrollNext && (
                  <button
                    onClick={() => api?.scrollNext()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-[#003a70]/80 rounded-full flex items-center justify-center text-white shadow-lg"
                  >
                    <ChevronRight size={16} />
                  </button>
                )}
              </>
            )}

            <Carousel 
              setApi={setApi} 
              className="w-full" 
              opts={{
                align: "start",
                loop: true,
                skipSnaps: false,
                dragFree: true
              }}
            >
              <CarouselContent className={cn(
                isMobile ? "-ml-1 pl-4 pr-4" : "-ml-2 md:-ml-4"
              )}>
                {demoTemplates.map((template, index) => (
                  <CarouselItem 
                    key={template.id} 
                    className={cn(
                      "flex",
                      isMobile 
                        ? "pl-1 basis-[85%]" // Show peek of next card on mobile
                        : "pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
                    )}
                  >
                    <Card className="group relative overflow-hidden bg-white shadow-2xl hover:shadow-3xl transition-all duration-700 border border-gray-200/50 hover:border-[#FEE6D8]/60 transform hover:-translate-y-4 hover:scale-[1.02] flex flex-col w-full backdrop-blur-sm">
                      {/* Enhanced depth background */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-transparent to-gray-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      
                      {/* Subtle glow effect */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-[#FEE6D8]/20 via-[#003a70]/10 to-[#FEE6D8]/20 rounded-xl blur-sm opacity-0 group-hover:opacity-60 transition-all duration-500 -z-10"></div>
                      
                      <CardContent className="p-0 flex flex-col h-full relative z-10">
                        
                        {/* Image Preview */}
                        <div className="relative overflow-hidden flex-shrink-0 shadow-lg">
                          <AspectRatio ratio={16 / 10}>
                            <img 
                              src={template.image} 
                              alt={template.title} 
                              className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110" 
                            />
                            
                            {/* Enhanced overlay with gradient */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                              <div className="absolute bottom-3 md:bottom-4 left-3 md:left-4 right-3 md:right-4">
                                <div className="flex items-center gap-2 text-white drop-shadow-lg">
                                  <Eye size={14} className="animate-pulse" />
                                  <span className="text-xs md:text-sm font-medium">Template Preview</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-full group-hover:translate-x-[-200%] group-hover:duration-1000"></div>
                          </AspectRatio>
                        </div>

                        {/* Content with enhanced depth */}
                        <div className="p-4 md:p-6 space-y-3 md:space-y-4 flex flex-col flex-grow relative bg-gradient-to-b from-white to-gray-50/50 shadow-inner">
                          {/* Subtle inner glow */}
                          <div className="absolute inset-0 bg-gradient-to-br from-[#FEE6D8]/5 via-transparent to-[#003a70]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-lg"></div>
                          
                          <div className="flex-grow relative z-10">
                            <h3 className="text-lg md:text-xl font-bold text-gray-900 group-hover:text-[#003a70] transition-all duration-500 mb-2 leading-tight drop-shadow-sm">
                              {template.title}
                            </h3>
                          </div>

                          {/* Live Demo Button with enhanced styling */}
                          <div className="pt-3 md:pt-4 border-t border-gray-100/80 mt-auto relative z-10">
                            <Button 
                              variant="outline" 
                              className="w-full group/btn bg-[#003a70] border-[#003a70] text-white hover:bg-[#002852] hover:border-[#002852] transition-all duration-500 text-sm md:text-base py-2 md:py-3 shadow-lg hover:shadow-xl transform hover:scale-[1.02] hover:-translate-y-0.5" 
                              onClick={() => handleDemoClick(template)} 
                              disabled={!template.embedUrl}
                            >
                              <ExternalLink size={14} className="mr-2 transition-transform duration-300 group-hover/btn:scale-110 group-hover/btn:rotate-12" />
                              {template.embedUrl ? "Live Demo" : "Coming Soon"}
                              
                              {/* Button shine effect */}
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 transform -skew-x-12 translate-x-full group-hover/btn:translate-x-[-200%] group-hover/btn:duration-700"></div>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              
              {/* Navigation - Only show on desktop */}
              {!isMobile && (
                <>
                  <CarouselPrevious className="absolute -left-4 top-1/2 -translate-y-1/2 bg-white border-2 border-[#FEE6D8] text-[#003a70] hover:bg-[#FEE6D8] hover:text-[#003a70] shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110" />
                  <CarouselNext className="absolute -right-4 top-1/2 -translate-y-1/2 bg-white border-2 border-[#FEE6D8] text-[#003a70] hover:bg-[#FEE6D8] hover:text-[#003a70] shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110" />
                </>
              )}
            </Carousel>

            {/* Enhanced Progress Indicators with Background Bar and Fraction */}
            <div className="flex flex-col items-center mt-6 md:mt-8 gap-3">
              {/* Fraction Text */}
              <div className="text-[#003a70] font-semibold text-sm">
                {currentIndex + 1} / {demoTemplates.length}
              </div>
              
              {/* Progress Bar Background */}
              <div className="relative w-20 h-1 bg-[#FEE6D8]/50 rounded-full overflow-hidden">
                <div 
                  className="absolute top-0 left-0 h-full bg-[#003a70] rounded-full transition-all duration-300"
                  style={{ width: `${((currentIndex + 1) / demoTemplates.length) * 100}%` }}
                />
              </div>
              
              {/* Enhanced Dot Indicators */}
              <div className="flex justify-center gap-2">
                {demoTemplates.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "rounded-full transition-all duration-300 shadow-md hover:shadow-lg border-2",
                      index === currentIndex 
                        ? "w-4 h-4 bg-[#003a70] border-[#003a70] scale-125 shadow-lg" 
                        : "w-3 h-3 bg-[#FEE6D8] border-[#FEE6D8] hover:bg-[#003a70]/50 hover:border-[#003a70]/50 hover:scale-110"
                    )}
                    onClick={() => api?.scrollTo(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Modal */}
      {selectedDemo && (
        <DemoModal 
          isOpen={modalOpen} 
          onClose={handleModalClose} 
          embedUrl={selectedDemo.embedUrl} 
          title={selectedDemo.title} 
        />
      )}
    </section>
  );
};

export default AILandingPagesGallery;
